import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, User, AlertCircle, Key, Loader2, Clock } from 'lucide-react';
import clientPortalService from '../services/clientPortalService';
import { webauthnService } from '../services/webauthnService';

const Login = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // One form for everyone. The old staff/client toggle keyed off
  // hostname === 'virgolaw.app', which is the MARKETING host and never serves
  // this bundle — so the check was permanently false and the toggle always
  // showed. Which namespace an identifier belongs to is the server's business,
  // and it is only answered after the password checks out.
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [status, setStatus] = useState(null);

  // Unauthenticated status probe. Two signals in one: any banner staff have set,
  // and — more importantly — whether the API answers at all. A locked-out client
  // is the only person who sees this page, so it has to be able to tell them
  // something is wrong without them logging in first.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/public/portal-status', { cache: 'no-store' });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (!cancelled && data && data.message) {
          setStatus({ message: data.message, severity: data.severity });
        }
      } catch (e) {
        if (!cancelled) {
          setStatus({
            message:
              "We are having trouble reaching our systems. Sign-in may not work right now — please call the office if you need us.",
            severity: 'error',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Check for session expired query param
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setSessionExpired(true);
      // Clear the query param from URL without reload
      searchParams.delete('expired');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle demo magic link redirect: ?demo_access=1&ref=<signup_id>
  useEffect(() => {
    const isDemoAccess = searchParams.get('demo_access') === '1';
    const ref = searchParams.get('ref');

    if (!isDemoAccess || !ref) return;

    // Clear params from URL immediately so a refresh doesn't re-trigger
    searchParams.delete('demo_access');
    searchParams.delete('ref');
    setSearchParams(searchParams, { replace: true });

    const autoDemoLogin = async () => {
      setLoading(true);
      setError('');
      try {
        // Exchange verified signup ref for a JWT — no password needed
        const res = await fetch('/api/auth/demo-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ref: parseInt(ref, 10) }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Demo login failed.');
        }

        const data = await res.json();

        // Store token the same way AuthContext.login() does
        localStorage.setItem('token', data.access_token);
        const sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('sessionId', sessionId);
        localStorage.removeItem('whileYouWereGoneDismissed');

        // Fetch user profile so AuthContext can hydrate on reload
        const { authService } = await import('../services/authService');
        const userData = await authService.getCurrentUser();
        localStorage.setItem('user', JSON.stringify(userData));

        // Hard reload — AuthContext re-hydrates from localStorage + httpOnly cookie
        window.location.href = '/';
      } catch (err) {
        setError(err.message || 'Demo access failed. Please request a new link.');
        setLoading(false);
      }
    };

    autoDemoLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only

  // WebAuthn state
  const [webauthnEnabled, setWebauthnEnabled] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [userId, setUserId] = useState(null);
  const [webauthnLoading, setWebauthnLoading] = useState(false);

  const { login, loginWithWebAuthn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Resolve identifier -> namespace server-side, then route on the result.
      // Never route from a lookup: which namespace an address belongs to is not
      // disclosed until the credentials are proven.
      const res = await fetch('/api/auth/resolve-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier,
          password,
          totp_code: totpCode || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error('login failed');
        err.response = { status: res.status, data };
        throw err;
      }
      if (data.role === 'client') {
        // Password accepted; a code was emailed. PortalLogin owns the code step.
        clientPortalService.stashPendingLogin(data);
        navigate(data.destination || '/portal/login', { replace: true });
      } else {
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
        }
        window.location.assign(data.destination || '/');
      }
    } catch (err) {
      console.error('Login error:', err);

      if (err.response?.status === 401) {
        const detail = err.response?.data?.detail;

        // Check if it's the new structured 2FA response
        if (detail && typeof detail === 'object' && detail.message === '2FA code required') {
          setRequire2FA(true);
          setTotpEnabled(detail.totp_enabled || false);
          setWebauthnEnabled(detail.webauthn_enabled || false);
          setUserId(detail.user_id);

          if (detail.webauthn_enabled && !detail.totp_enabled) {
            setError('Please use your security key to continue');
          } else if (detail.webauthn_enabled && detail.totp_enabled) {
            setError('Please enter your 2FA code or use your security key');
          } else {
            setError('Please enter your 2FA code');
          }
        } else if (detail === '2FA code required') {
          // Legacy string response
          setRequire2FA(true);
          setTotpEnabled(true);
          setError('Please enter your 2FA code');
        } else if (detail === 'Invalid 2FA code') {
          setError('Invalid 2FA code. Please try again.');
        } else if (detail === 'Security key verification failed') {
          setError('Security key verification failed. Please try again.');
        } else {
          // One message for every failure. Distinguishing them tells an
          // attacker whether an address belongs to a client of this firm.
          setError('We could not sign you in. Check your details and try again.');
        }
      } else if (err.response?.status === 403) {
        setError(err.response?.data?.detail || 'Access denied');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWebAuthnLogin = async () => {
    if (!userId) {
      setError('Please enter your credentials first');
      return;
    }

    setWebauthnLoading(true);
    setError('');

    try {
      // Get WebAuthn authentication options from server
      const { options } = await webauthnService.beginAuthentication(identifier);

      // Parse options for browser
      const publicKeyOptions = parseRequestOptions(options);

      // Prompt user for security key
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      if (!credential) {
        setError('Security key authentication was cancelled');
        return;
      }

      // Format credential for server
      const formattedCredential = formatCredentialForServer(credential);

      // Complete login with WebAuthn
      if (loginWithWebAuthn) {
        await loginWithWebAuthn(identifier, password, formattedCredential);
      } else {
        await login(identifier, password, null, formattedCredential);
      }
      navigate(returnTo || '/');
    } catch (err) {
      console.error('WebAuthn login error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Security key authentication was cancelled or timed out');
      } else {
        setError(err.response?.data?.detail || err.message || 'Security key authentication failed');
      }
    } finally {
      setWebauthnLoading(false);
    }
  };

  // Helper functions for WebAuthn
  const base64URLToBuffer = (base64url) => {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    const binary = atob(paddedBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const bufferToBase64URL = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const parseRequestOptions = (options) => {
    const opts = typeof options === 'string' ? JSON.parse(options) : options;
    return {
      ...opts,
      challenge: base64URLToBuffer(opts.challenge),
      allowCredentials: (opts.allowCredentials || []).map(cred => ({
        ...cred,
        id: base64URLToBuffer(cred.id),
      })),
    };
  };

  const formatCredentialForServer = (credential) => {
    const response = credential.response;
    return {
      id: credential.id,
      rawId: bufferToBase64URL(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64URL(response.clientDataJSON),
        authenticatorData: bufferToBase64URL(response.authenticatorData),
        signature: bufferToBase64URL(response.signature),
        userHandle: response.userHandle ? bufferToBase64URL(response.userHandle) : null,
      },
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-teal-900 flex items-center justify-center px-4">
      <div className="bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <img
              src="/assets/images/virgo_logo.jpg"
              alt="Virgo"
              className="h-24 w-24 rounded-lg object-cover ring-4 ring-virgo-gold-400 shadow-xl"
            />
          </div>
          <h1 className="text-3xl font-bold text-virgo-gold-400 mb-2">VIRGO</h1>
          <p className="text-virgo-teal-300">Legal Practice Management</p>
        </div>

        {/* Unauthenticated status line. Its absence is why a portal
            outage stayed invisible for days — the people who hit it were
            the only ones who could see it, and they could not log in to
            report it. */}
        {status && status.message && (
          <div className="mb-4 p-3 bg-amber-900/30 border border-amber-600 text-amber-200 rounded-lg text-sm">
            {status.message}
          </div>
        )}

        {sessionExpired && (
          <div className="mb-4 p-3 bg-amber-900/30 border border-amber-600 text-amber-300 rounded-lg flex items-center">
            <Clock size={20} className="mr-2 flex-shrink-0" />
            <span>Your session has expired. Please log in again.</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-700 text-red-300 rounded-lg flex items-center">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
              Email or username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-virgo-teal-400" size={20} />
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                className="w-full pl-10 pr-4 py-2 bg-virgo-navy-800 border border-virgo-teal-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent placeholder-virgo-teal-600"
                placeholder="you@example.com"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-virgo-teal-400" size={20} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-virgo-navy-800 border border-virgo-teal-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent placeholder-virgo-teal-600"
                placeholder="Enter your password"
                required
                disabled={loading}
              />
            </div>
          </div>

          {require2FA && (
            <div className="space-y-4">
              {/* TOTP Code Input - only show if TOTP is enabled */}
              {totpEnabled && (
                <div>
                  <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
                    2FA Code
                  </label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    className="w-full px-4 py-2 bg-virgo-navy-800 border border-virgo-teal-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 focus:border-transparent placeholder-virgo-teal-600"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    disabled={loading || webauthnLoading}
                  />
                </div>
              )}

              {/* Security Key Option - only show if WebAuthn is enabled */}
              {webauthnEnabled && (
                <div className="pt-2">
                  {totpEnabled && (
                    <div className="relative mb-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-virgo-teal-700"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-virgo-navy-700 text-virgo-teal-400">or</span>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleWebAuthnLogin}
                    disabled={loading || webauthnLoading}
                    className="w-full flex items-center justify-center py-3 bg-gradient-to-r from-amber-600 to-yellow-500 text-white rounded-lg font-semibold hover:from-amber-500 hover:to-yellow-400 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {webauthnLoading ? (
                      <>
                        <Loader2 size={20} className="mr-2 animate-spin" />
                        Touch your security key...
                      </>
                    ) : (
                      <>
                        <Key size={20} className="mr-2" />
                        Use Security Key
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg font-semibold transition-all shadow-lg disabled:from-gray-600 disabled:to-gray-500 disabled:cursor-not-allowed bg-gradient-to-r from-virgo-teal-600 to-virgo-teal-500 text-white hover:from-virgo-teal-500 hover:to-virgo-teal-400 shadow-virgo-teal-900/50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Every failure now looks the same on purpose, so recovery has to
            be obvious rather than inferred from an error message. */}
        <div className="mt-6 text-center space-y-2">
          <Link
            to="/portal/forgot-password"
            className="block text-virgo-gold-400 hover:text-virgo-gold-300 text-sm font-semibold transition-colors"
          >
            Trouble signing in?
          </Link>
          <Link
            to="/forgot-password"
            className="block text-virgo-teal-500 hover:text-virgo-teal-400 text-xs transition-colors"
          >
            Staff password reset
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
